import React, { useMemo, useState } from 'react';
import { 
  Users, Car, FileText, DollarSign, CreditCard, AlertCircle, Wrench, MapPin, Phone, Send, Trash2 
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { sendTelegramNotification } from '../../../utils/telegram';
import { isHiddenModule } from '../../../config/hiddenModules';

export default function Dashboard({ 
  settings = {}, 
  activeAlerts = [], 
  setCurrentPage, 
  globalCars = [], 
  globalClients = [], 
  globalRentals = [], 
  globalVidanges = [],
  onDismissAlert = () => {}
}) {
  const { t, lang } = useLanguage();
  const [upcomingKm, setUpcomingKm] = useState(1000);

  const MAINTENANCE_TYPES = [
    { key: 'Vidange', fr: 'Vidange d\'huile', ar: 'تغيير الزيت' },
    { key: 'Filtre à huile', fr: 'Filtre à huile', ar: 'فلتر الزيت' },
    { key: 'Filtre à air', fr: 'Filtre à air', ar: 'فلتر الهواء' },
    { key: 'Filtre à carburant', fr: 'Filtre à carburant', ar: 'فلتر الوقود' },
    { key: 'Courroie de distribution', fr: 'Courroie de distribution', ar: 'حزام التوقيت' },
    { key: 'Pneus', fr: 'Pneus', ar: 'الإطارات' },
    { key: 'Freins', fr: 'Freins', ar: 'الفرامل' },
    { key: 'Bougies', fr: 'Bougies', ar: 'الشمعات' },
    { key: 'Batterie', fr: 'Batterie', ar: 'البطارية' },
    { key: 'Climatisation', fr: 'Climatisation', ar: 'التكييف' },
    { key: 'Autre', fr: 'Autre', ar: 'أخرى' }
  ];

  const maintWarning = settings.maintWarningThreshold ?? 80;
  const maintCritical = settings.maintCriticalThreshold ?? 90;
  const maintOverdue = settings.maintOverdueThreshold ?? 100;

  const getDefaultInterval = (type) => {
    const intervals = settings?.maintIntervals || {};
    if (intervals[type] !== undefined) return parseFloat(intervals[type]) || 10000;
    switch(type) {
      case 'Vidange':
      case 'Filtre à huile': return 10000;
      case 'Filtre à air':
      case 'Climatisation': return 20000;
      case 'Filtre à carburant':
      case 'Freins':
      case 'Bougies': return 30000;
      case 'Pneus': return 40000;
      case 'Batterie': return 60000;
      case 'Courroie de distribution': return 80000;
      default: return 10000;
    }
  };

  const maintStats = useMemo(() => {
    let overdueVehicles = 0;
    let dueSoonVehicles = 0;
    let goodVehicles = 0;
    let unknownVehicles = 0;
    const upcomingItemsList = [];

    globalCars.forEach(car => {
      const effectiveKm = (parseFloat(car.km) || 0) + (parseFloat(car.gpsOdometer) || 0);
      const carVidanges = globalVidanges.filter(v => v.carId === car.id || v.carId === car._id);
      
      let carOverdue = false;
      let carDueSoon = false;
      let hasRecords = false;

      MAINTENANCE_TYPES.forEach(item => {
        const typeMaint = carVidanges.filter(v => v.type === item.key);
        typeMaint.sort((a, b) => (parseFloat(b.currentKm) || 0) - (parseFloat(a.currentKm) || 0));
        const latest = typeMaint[0] || null;

        let lastKm = 0;
        let nextKm = 0;
        if (latest) {
          lastKm = parseFloat(latest.currentKm) || 0;
          nextKm = parseFloat(latest.nextKm) || 0;
          hasRecords = true;
        } else {
          lastKm = 0;
          nextKm = getDefaultInterval(item.key);
        }

        const interval = nextKm - lastKm;
        const driven = Math.max(0, effectiveKm - lastKm);
        const remaining = nextKm - effectiveKm;
        const pct = interval > 0 ? Math.round((driven / interval) * 100) : 0;

        if (pct >= maintOverdue) {
          carOverdue = true;
        } else if (pct >= maintWarning) {
          carDueSoon = true;
        }

        // Check for upcoming within X km (not already overdue)
        if (remaining > 0 && remaining <= upcomingKm && pct < maintOverdue) {
          upcomingItemsList.push({
            carId: car.id,
            carName: `${car.brand} ${car.model}`,
            plate: car.plate,
            type: item.key,
            fr: item.fr,
            ar: item.ar,
            remaining,
            pct,
            color: pct >= maintCritical ? '#f97316' : pct >= maintWarning ? '#f59e0b' : '#10b981'
          });
        }
      });

      if (carOverdue) overdueVehicles++;
      else if (carDueSoon) dueSoonVehicles++;
      else if (hasRecords) goodVehicles++;
      else unknownVehicles++;
    });

    upcomingItemsList.sort((a, b) => a.remaining - b.remaining);

    return {
      overdue: overdueVehicles,
      dueSoon: dueSoonVehicles,
      good: goodVehicles,
      unknown: unknownVehicles,
      upcoming: upcomingItemsList
    };
  }, [globalCars, globalVidanges, settings, upcomingKm]);

  // Donut SVG layout values
  const totalM = maintStats.overdue + maintStats.dueSoon + maintStats.good + maintStats.unknown || 1;
  const overduePct = (maintStats.overdue / totalM) * 100;
  const dueSoonPct = (maintStats.dueSoon / totalM) * 100;
  const goodPct = (maintStats.good / totalM) * 100;
  const unknownPct = (maintStats.unknown / totalM) * 100;

  const r = 40;
  const circ = 2 * Math.PI * r;
  const strokeOverdue = (overduePct / 100) * circ;
  const strokeDueSoon = (dueSoonPct / 100) * circ;
  const strokeGood = (goodPct / 100) * circ;
  const strokeUnknown = (unknownPct / 100) * circ;

  const offsetOverdue = 0;
  const offsetDueSoon = strokeOverdue;
  const offsetGood = strokeOverdue + strokeDueSoon;
  const offsetUnknown = strokeOverdue + strokeDueSoon + strokeGood;

  const stats = useMemo(() => {
    const clientsCount = globalClients.length;
    const carsCount = globalCars.length;
    const activeRentalsCount = globalRentals.filter(r => r.status === 'active').length;
    const revenue = globalRentals.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0);
    const pendingCredits = globalCars.filter(c => c.credit && c.creditRemaining > 0).length;
    
    return {
      clients: clientsCount,
      cars: carsCount,
      activeRentals: activeRentalsCount,
      revenue,
      pendingCredits
    };
  }, [globalClients, globalCars, globalRentals]);

  const recentRentals = useMemo(() => {
    return [...globalRentals]
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
      .slice(0, 5);
  }, [globalRentals]);

  const cards = [
    { label: t('clients_count'), value: stats.clients, icon: Users, color: 'blue' },
    { label: t('cars_count'), value: stats.cars, icon: Car, color: 'green' },
    { label: t('active_rentals'), value: stats.activeRentals, icon: FileText, color: 'amber' },
    { label: `${t('revenue')} (${settings.currency || 'MAD'})`, value: stats.revenue.toLocaleString(), icon: DollarSign, color: 'purple' },
    { label: t('pending_credits'), value: stats.pendingCredits, icon: CreditCard, color: 'red' },
  ];

  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  const getWhatsAppLink = (phone, carName, clientName, endDate) => {
    if (!phone) return '';
    let clean = phone.replace(/\s+/g, '');
    if (clean.startsWith('0')) {
      clean = '212' + clean.slice(1);
    }
    const message = `Salam ${clientName}, CAR TRACK APP vous informe que la date de restitution du véhicule ${carName} (prévue le ${endDate}) est dépassée. Merci de nous contacter au plus vite pour régulariser la situation.`;
    return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
  };

  // Hide alerts (and their quick-action buttons) that navigate to a hidden module.
  const visibleAlerts = activeAlerts.filter(a => !(a.type === 'vidange' && isHiddenModule('vidange')));

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('dashboard')}</h1>
      
      {/* Premium Smart Alerts Center */}
      {visibleAlerts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
          <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-red-50 text-red-500 rounded-lg animate-pulse">
                <AlertCircle className="w-5 h-5" />
              </div>
              <h2 className="font-bold text-gray-900 text-base">{t('smart_alerts')}</h2>
            </div>
            <span className="px-2.5 py-0.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-full">
              {visibleAlerts.length} {visibleAlerts.length === 1 ? 'alerte active' : 'alertes actives'}
            </span>
          </div>
          
          <div className="space-y-3">
            {visibleAlerts.map(alert => {
              const isRed = alert.severity === 'red';
              const borderClass = isRed ? 'border-red-100 bg-red-50/10' : 'border-amber-100 bg-amber-50/10';
              const iconBg = isRed ? 'bg-red-50 text-red-500' : 'bg-amber-50 text-amber-500';
              
              return (
                <div 
                  key={alert.id} 
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border ${borderClass} hover:shadow-md transition-shadow duration-200`}
                >
                  <div className="flex items-start gap-3.5">
                    <div className={`p-2.5 rounded-xl shrink-0 ${iconBg}`}> 
                      {alert.type === 'vidange' ? <Wrench className="w-5 h-5" /> :
                       alert.type === 'gps' ? <MapPin className="w-5 h-5 animate-bounce" /> :
                       <AlertCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-extrabold rounded-md uppercase tracking-wider mb-1 ${
                        isRed ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}> 
                        {alert.type === 'vidange' ? '🔧 Vidange Proche' :
                         alert.type === 'gps' ? '📡 Traceur GPS Déconnecté' :
                         '⏳ Client en Retard'}
                      </span>
                      <h3 className="font-bold text-gray-900 text-sm mt-0.5">{alert.title}</h3>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{alert.message}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                    {alert.type === 'vidange' && (
                      <button
                        onClick={() => setCurrentPage('vidange')}
                        className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-xs font-bold transition shadow-sm hover:shadow shadow-amber-500/20 cursor-pointer"
                      >
                        <Wrench className="w-3.5 h-3.5" />
                        {alert.actionText || 'Enregistrer vidange'}
                      </button>
                    )}
                    
                    {alert.type === 'gps' && (
                      <button
                        onClick={() => setCurrentPage('gps')}
                        className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold transition shadow-sm hover:shadow shadow-red-500/20 cursor-pointer"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {alert.actionText || 'Voir GPS'}
                      </button>
                    )}
                    
                    {alert.type === 'retard' && (
                      <div className="flex items-center gap-2">
                        {alert.clientPhone && (
                          <a
                            href={`tel:${alert.clientPhone}`}
                            className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white px-3.5 py-2 rounded-lg text-xs font-bold transition shadow-sm hover:shadow shadow-blue-500/20 cursor-pointer"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Appeler
                          </a>
                        )}
                        {alert.clientPhone && (
                          <a
                            href={getWhatsAppLink(alert.clientPhone, alert.carName, alert.clientName, alert.endDate)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-3.5 py-2 rounded-lg text-xs font-bold transition shadow-sm hover:shadow shadow-emerald-500/20 cursor-pointer"
                          >
                            <Send className="w-3.5 h-3.5" />
                            WhatsApp
                          </a>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => onDismissAlert(alert.id)}
                      className="flex items-center gap-1.5 bg-gray-100 hover:bg-red-50 hover:text-red-600 text-gray-500 px-3.5 py-2 rounded-lg text-xs font-bold transition cursor-pointer border-none"
                      title={lang === 'ar' ? 'إلغاء التنبيه' : 'Masquer l\'alerte'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{lang === 'ar' ? 'إلغاء' : 'Ignorer'}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">{c.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{c.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${colors[c.color]}`}>
                <c.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 🔧 Fleet Maintenance Dashboard Section — hidden while the Vidange module is disabled */}
      {!isHiddenModule('vidange') && (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
        <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-blue-50 text-blue-500 rounded-lg">
              <Wrench className="w-5 h-5" />
            </div>
            <h2 className="font-bold text-gray-900 text-base">
              {lang === 'ar' ? 'تقرير حالة الصيانة والزيوت للأسطول' : 'Statut Maintenance & Vidanges de la Flotte'}
            </h2>
          </div>
          <button
            onClick={() => setCurrentPage('vidange')}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 transition bg-transparent border-none cursor-pointer"
          >
            {lang === 'ar' ? 'إدارة الصيانة ←' : 'Gérer la maintenance →'}
          </button>
        </div>

        {/* Aggregate maintenance numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 text-center">
            <span className="text-xl font-mono font-black text-gray-900">{globalCars.length}</span>
            <span className="block text-[10px] text-gray-500 font-bold mt-1 uppercase">{lang === 'ar' ? 'إجمالي الأسطول' : 'Flotte totale'}</span>
          </div>
          <div className="bg-red-50/30 p-4 rounded-xl border border-red-100 text-center">
            <span className="text-xl font-mono font-black text-red-600">{maintStats.overdue}</span>
            <span className="block text-[10px] text-red-500 font-bold mt-1 uppercase">{lang === 'ar' ? 'صيانة متأخرة' : 'Entretien dépassé'}</span>
          </div>
          <div className="bg-amber-50/30 p-4 rounded-xl border border-amber-100 text-center">
            <span className="text-xl font-mono font-black text-amber-600">{maintStats.dueSoon}</span>
            <span className="block text-[10px] text-amber-500 font-bold mt-1 uppercase">{lang === 'ar' ? 'خدمة قريبة' : 'Dû bientôt'}</span>
          </div>
          <div className="bg-green-50/30 p-4 rounded-xl border border-green-100 text-center">
            <span className="text-xl font-mono font-black text-green-600">{maintStats.good}</span>
            <span className="block text-[10px] text-green-500 font-bold mt-1 uppercase">{lang === 'ar' ? 'صيانة جاهزة' : 'Entretiens à jour'}</span>
          </div>
        </div>

        {/* Visual Report Donut & Upcoming items list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* SVG Donut Chart */}
          <div className="lg:col-span-4 flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl bg-gray-50/30">
            <h3 className="text-xs font-black text-gray-600 mb-4 uppercase tracking-wider">{lang === 'ar' ? 'مؤشر صحة الأسطول' : 'État de Santé Global'}</h3>
            <div className="relative w-36 h-36">
              <svg className="w-full h-full transform -rotate-95" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f3f4f6" strokeWidth="10" />
                
                {/* Good circle (green) */}
                {strokeGood > 0 && (
                  <circle 
                    cx="50" cy="50" r="40" fill="transparent" stroke="#10b981" strokeWidth="10"
                    strokeDasharray={`${strokeGood} ${circ}`}
                    strokeDashoffset={-offsetGood}
                    strokeLinecap="round"
                  />
                )}

                {/* Due soon circle (yellow/orange) */}
                {strokeDueSoon > 0 && (
                  <circle 
                    cx="50" cy="50" r="40" fill="transparent" stroke="#f59e0b" strokeWidth="10"
                    strokeDasharray={`${strokeDueSoon} ${circ}`}
                    strokeDashoffset={-offsetDueSoon}
                    strokeLinecap="round"
                  />
                )}

                {/* Overdue circle (red) */}
                {strokeOverdue > 0 && (
                  <circle 
                    cx="50" cy="50" r="40" fill="transparent" stroke="#ef4444" strokeWidth="10"
                    strokeDasharray={`${strokeOverdue} ${circ}`}
                    strokeDashoffset={-offsetOverdue}
                    strokeLinecap="round"
                  />
                )}

                {/* Unknown circle (gray) */}
                {strokeUnknown > 0 && (
                  <circle 
                    cx="50" cy="50" r="40" fill="transparent" stroke="#9ca3af" strokeWidth="10"
                    strokeDasharray={`${strokeUnknown} ${circ}`}
                    strokeDashoffset={-offsetUnknown}
                    strokeLinecap="round"
                  />
                )}
              </svg>
              {/* Inner details */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-xl font-black text-gray-900 font-mono">
                  {Math.round((maintStats.good / totalM) * 100)}%
                </span>
                <span className="text-[9px] text-gray-400 font-bold uppercase">{lang === 'ar' ? 'سليم' : 'A jour'}</span>
              </div>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-2 mt-4 text-[10px] w-full max-w-[200px]">
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10b981] inline-block"></span>
                <span className="text-gray-500 font-medium">{lang === 'ar' ? 'سليم' : 'A jour'}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b] inline-block"></span>
                <span className="text-gray-500 font-medium">{lang === 'ar' ? 'قريب' : 'Bientôt'}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ef4444] inline-block"></span>
                <span className="text-gray-500 font-medium">{lang === 'ar' ? 'متأخر' : 'En retard'}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#9ca3af] inline-block"></span>
                <span className="text-gray-500 font-medium">{lang === 'ar' ? 'غير محدد' : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Interactive upcoming search */}
          <div className="lg:col-span-8 flex flex-col space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-xs font-black text-gray-600 uppercase tracking-wider">
                ⏳ {lang === 'ar' ? `الصيانات المطلوبة خلال الـ X كيلومتر القادمة` : 'Entretiens à prévoir bientôt'}
              </h3>
              
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{lang === 'ar' ? 'خلال' : 'Dans les'}</span>
                <div className="relative">
                  <input
                    type="number"
                    value={upcomingKm}
                    onChange={e => setUpcomingKm(Math.max(100, parseInt(e.target.value) || 0))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded-lg text-xs font-mono text-center font-bold focus:ring-2 focus:ring-blue-500 outline-none text-blue-700 bg-blue-50/50"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-blue-500 pointer-events-none">km</span>
                </div>
              </div>
            </div>

            {/* List of upcoming items */}
            <div className="border border-gray-150 rounded-xl overflow-hidden divide-y divide-gray-100 max-h-[220px] overflow-y-auto">
              {maintStats.upcoming.length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-400 italic">
                  {lang === 'ar' 
                    ? `لا توجد صيانة مجدولة خلال الـ ${upcomingKm.toLocaleString()} كم القادمة.`
                    : `Aucun entretien de prévu dans les prochaines ${upcomingKm.toLocaleString()} km.`}
                </div>
              ) : (
                maintStats.upcoming.map((item, idx) => {
                  const typeLabel = lang === 'ar' ? item.ar : item.fr;
                  return (
                    <div key={`${item.carId}-${item.type}-${idx}`} className="p-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <div className="min-w-0 pr-4">
                        <div className="font-bold text-xs text-gray-900 truncate">
                          {item.carName}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5 flex items-center gap-1.5">
                          <span className="font-semibold">{item.plate}</span>
                          <span className="text-blue-500 bg-blue-50 px-1 py-0.5 rounded text-[8px] font-bold uppercase">{typeLabel}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-xs font-black`} style={{ color: item.color }}>
                          {lang === 'ar' ? `خلال ${Math.round(item.remaining).toLocaleString()} كم` : `Dans ${Math.round(item.remaining).toLocaleString()} km`}
                        </div>
                        <div className="w-20 bg-gray-100 h-1.5 rounded-full overflow-hidden mt-1.5 ml-auto">
                          <div className="h-full rounded-full" style={{ width: `${item.pct}%`, backgroundColor: item.color }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Recent Rentals List */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-bold text-gray-900 mb-4">{t('recent_rentals')}</h2>
        {recentRentals.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune location pour l'instant.</p>
        ) : (
          <div className="space-y-3">
            {recentRentals.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium text-sm">{`${r.clientName} - ${r.carName}`}</p>
                  <p className="text-xs text-gray-500">{`${r.startDate} → ${r.endDate}`}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  r.status === 'active' ? 'bg-green-100 text-green-700' :
                  r.status === 'completed' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {r.status === 'active' ? t('status_active') : r.status === 'completed' ? t('status_completed') : t('status_cancelled')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
